/**
 * Native chrome for the Capacitor shell.
 * Colours are read from the live desk tokens (--ns-topbar-bg, --rail), not a second palette.
 */

export function barColorFromCss(value: string, fallback: string): string {
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    const h = v.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  const rgb = v.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!rgb) return fallback;
  const hex = (n: string) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, "0");
  return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
}

/** Light status-bar icons when the bar itself is dark. */
export function lightContentOn(hex: string): boolean {
  const n = hex.replace("#", "");
  if (n.length !== 6) return true;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const y = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return y < 0.62;
}

export type NativeBarColors = {
  status: string;
  navigation: string;
  lightStatus: boolean;
  lightNavigation: boolean;
};

const DAY_STATUS = "#215085";
const NIGHT_STATUS = "#1f4c80";

export function readNativeBarColors(
  target: { getAttribute?(name: string): string | null; nodeType?: number } | null | undefined,
): NativeBarColors {
  const theme = target?.getAttribute?.("data-theme") === "dark" ? "dark" : "light";
  const fallbackStatus = theme === "dark" ? NIGHT_STATUS : DAY_STATUS;
  let status = fallbackStatus;
  let navigation = theme === "dark" ? "#14304e" : "#e3ebf5";
  if (target && target.nodeType === 1 && typeof getComputedStyle === "function") {
    const css = getComputedStyle(target as Element);
    status = barColorFromCss(css.getPropertyValue("--ns-topbar-bg") || css.getPropertyValue("--chrome"), fallbackStatus);
    navigation = barColorFromCss(css.getPropertyValue("--rail"), navigation);
  }
  return {
    status,
    navigation,
    lightStatus: lightContentOn(status),
    lightNavigation: lightContentOn(navigation),
  };
}

type ShellPlugin = {
  setBars(options: { status: string; navigation: string; lightStatus: boolean; lightNavigation: boolean }): Promise<void>;
  installApk(options: { url: string }): Promise<void>;
};

export function isNativePlatform(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

async function shellPlugin(): Promise<ShellPlugin | null> {
  const { Capacitor, registerPlugin } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return null;
  return registerPlugin<ShellPlugin>("NorthstarShell");
}

export async function applyNativeBars(
  target: Element | null | undefined = typeof document !== "undefined" ? document.documentElement : null,
): Promise<void> {
  const plugin = await shellPlugin();
  if (!plugin) return;
  const colors = readNativeBarColors(target);
  await plugin.setBars(colors);
}

export async function installNativeApk(url: string): Promise<void> {
  const plugin = await shellPlugin();
  if (!plugin) throw new Error("APK install runs in the Android app.");
  await plugin.installApk({ url });
}

export async function exitNativeApp(): Promise<void> {
  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return;
  const { App } = await import("@capacitor/app");
  await App.exitApp();
}

export async function readNativeVersionCode(): Promise<number | null> {
  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return null;
  const { App } = await import("@capacitor/app");
  const info = await App.getInfo();
  const code = Number(info.build);
  return Number.isInteger(code) && code > 0 ? code : null;
}

/** Back button, system bars, and splash. No-op in a browser. */
export async function bootNativeShell(onBack: () => void): Promise<() => void> {
  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return () => undefined;
  const { App } = await import("@capacitor/app");
  const { SplashScreen } = await import("@capacitor/splash-screen");
  const handle = await App.addListener("backButton", () => onBack());
  await applyNativeBars();
  const root = document.documentElement;
  const observer = new MutationObserver(() => {
    void applyNativeBars(root);
  });
  observer.observe(root, { attributes: true, attributeFilter: ["data-theme", "data-theme-pack", "data-theme-accent"] });
  await SplashScreen.hide().catch(() => undefined);
  return () => {
    void handle.remove();
    observer.disconnect();
  };
}
